/**
 * RockPlanningBoard
 * Kanban-style planning board for Rocks. Columns map to BoardColumn values.
 * Drag-drop moves rocks between columns via useReorderRock mutation.
 */

import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { DragStartEvent, DragEndEvent, CollisionDetection } from '@dnd-kit/core';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useReorderRock } from '../hooks/useRocks';
import { BOARD_COLUMNS } from '../types';
import type { BoardColumn, Rock } from '../types';
import { RockBoardCard } from './RockBoardCard';

// =============================================================================
// Column styles
// =============================================================================

const COLUMN_STYLES: Record<BoardColumn, { headerBg: string; borderTop: string }> = {
  current: {
    headerBg: 'bg-blue-50 dark:bg-blue-950',
    borderTop: 'border-t-blue-500',
  },
  next: {
    headerBg: 'bg-violet-50 dark:bg-violet-950',
    borderTop: 'border-t-violet-500',
  },
  later: {
    headerBg: 'bg-amber-50 dark:bg-amber-950',
    borderTop: 'border-t-amber-400',
  },
  future: {
    headerBg: 'bg-slate-100 dark:bg-slate-800',
    borderTop: 'border-t-slate-400',
  },
  long_term_issues: {
    headerBg: 'bg-red-50 dark:bg-red-950',
    borderTop: 'border-t-red-400',
  },
};

// =============================================================================
// BoardColumnPanel
// =============================================================================

interface BoardColumnPanelProps {
  column: BoardColumn;
  label: string;
  rocks: Rock[];
  onEditRock: (rock: Rock) => void;
  onAddRock: (column: BoardColumn) => void;
}

function BoardColumnPanel({
  column,
  label,
  rocks,
  onEditRock,
  onAddRock,
}: BoardColumnPanelProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: column,
    data: { type: 'column', column },
  });

  const styles = COLUMN_STYLES[column];
  const rockIds = rocks.map((r) => r.id);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col min-w-[260px] w-[260px] rounded-lg border border-t-4 bg-muted/30',
        styles.borderTop,
        isOver && 'ring-2 ring-primary ring-offset-1'
      )}
    >
      {/* Column header */}
      <div className={cn('p-3 rounded-t-lg', styles.headerBg)}>
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm">{label}</h3>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground bg-background rounded-full px-2 py-0.5">
              {rocks.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => onAddRock(column)}
              title={`Add rock to ${label}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div
        className={cn(
          'flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-320px)]',
          isOver && 'bg-primary/5'
        )}
      >
        <SortableContext items={rockIds} strategy={verticalListSortingStrategy}>
          {rocks.map((rock) => (
            <RockBoardCard
              key={rock.id}
              rock={rock}
              onEdit={onEditRock}
            />
          ))}
        </SortableContext>

        {rocks.length === 0 && (
          <div className="flex items-center justify-center h-20 text-xs text-muted-foreground border-2 border-dashed rounded-lg">
            Drop rocks here
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// RockPlanningBoard
// =============================================================================

interface RockPlanningBoardProps {
  rocks: Rock[];
  onEditRock: (rock: Rock) => void;
}

export function RockPlanningBoard({ rocks, onEditRock }: RockPlanningBoardProps) {
  const reorderRock = useReorderRock();
  const [activeRockId, setActiveRockId] = useState<string | null>(null);

  // Group rocks by boardColumn
  const rocksByColumn: Record<BoardColumn, Rock[]> = {
    current: [],
    next: [],
    later: [],
    future: [],
    long_term_issues: [],
  };
  for (const rock of rocks) {
    const col = rock.boardColumn in rocksByColumn ? rock.boardColumn : 'current';
    rocksByColumn[col].push(rock);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    return rectIntersection(args);
  }, []);

  const activeRock = activeRockId
    ? rocks.find((r) => r.id === activeRockId) ?? null
    : null;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveRockId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveRockId(null);

      if (!over) return;

      const rockId = active.id as string;
      const overId = over.id as string;

      // Determine target column: over.id is either a column id or another rock id
      let targetColumn: BoardColumn | undefined;

      if (overId in rocksByColumn) {
        // Dropped directly on a column droppable
        targetColumn = overId as BoardColumn;
      } else {
        // Dropped on another rock — find which column contains that rock
        for (const [col, colRocks] of Object.entries(rocksByColumn)) {
          if (colRocks.some((r) => r.id === overId)) {
            targetColumn = col as BoardColumn;
            break;
          }
        }
      }

      if (!targetColumn) return;

      // Find the dragged rock to check if column actually changed
      const sourceRock = rocks.find((r) => r.id === rockId);
      if (!sourceRock) return;
      if (sourceRock.boardColumn === targetColumn) return;

      reorderRock.mutate({
        id: rockId,
        data: { board_column: targetColumn },
      });
    },
    [rocks, rocksByColumn, reorderRock]
  );

  const handleAddRock = useCallback((_column: BoardColumn) => {
    // No-op: Add rock form is triggered from parent (RocksPage) via onEditRock
    // with a new rock pre-populated for the column — wired in a later task.
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 overflow-x-auto pb-4 min-h-[500px]">
        {BOARD_COLUMNS.map(({ value, label }) => (
          <BoardColumnPanel
            key={value}
            column={value}
            label={label}
            rocks={rocksByColumn[value]}
            onEditRock={onEditRock}
            onAddRock={handleAddRock}
          />
        ))}
      </div>

      <DragOverlay>
        {activeRock && (
          <RockBoardCard rock={activeRock} onEdit={onEditRock} />
        )}
      </DragOverlay>
    </DndContext>
  );
}
